// @ts-nocheck
/**
 * Dialer Groups — unified hub with three tabs:
 *   1. Groups        — list of dialer groups (with inline Create / Edit / Delete)
 *   2. Assign Cos.   — bulk-assign companies to a group (formerly BulkGroupAddPage)
 *   3. Remove Cos.   — bulk-remove companies from a group (formerly BulkGroupDeletePage)
 *
 * Consolidates GroupListingPage + CreateGroupPage + BulkGroupAddPage +
 * BulkGroupDeletePage into one screen so users don't have to bounce between
 * four routes to manage groups and their memberships.
 *
 * Deep-link support: `?tab=groups|assign|remove` selects the initial tab
 * so the sidebar's three sub-links keep working.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil, Trash2, Plus, Download, UserPlus, UserMinus, Search as SearchIcon, Users } from 'lucide-react';
import { USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, formatDateTime } from '@/utilities/helpers';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import Loader from '@/components/partials/components/Loader';
import {
  API_MANAGE_GROUP_LISTING,
  API_MANAGE_GROUP_UPDATE,
  API_MANAGE_GROUP_DELETE,
  API_MANAGE_GROUP_CREATE,
  API_COMPANY_LIST,
  API_BULK_GROUP_ADD,
  API_BULK_GROUP_DELETE,
  API_GET_COMPANY_GROUP,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { getApiCall } from '@/service/getApiCall';
import { postApiCall } from '@/service/postApiCall';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useModuleAcl } from '@/hooks/useModuleAcl';

const TABS: { id: 'groups' | 'assign' | 'remove'; label: string; icon: any }[] = [
  { id: 'groups', label: 'Groups', icon: Users },
  { id: 'assign', label: 'Assign Companies', icon: UserPlus },
  { id: 'remove', label: 'Remove Companies', icon: UserMinus },
];

export default function GroupList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = (params.get('tab') as any) || 'groups';
  const [tab, setTab] = useState<'groups' | 'assign' | 'remove'>(
    ['groups', 'assign', 'remove'].includes(initialTab) ? initialTab : 'groups',
  );

  useEffect(() => {
    if (params.get('tab') !== tab) {
      const next = new URLSearchParams(params);
      next.set('tab', tab);
      setParams(next, { replace: true });
    }
  }, [tab]);

  // Shared state
  const [groups, setGroups] = useState<any[]>([]);
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [groupsRes, companiesRes] = await Promise.all([
          getApiCall(API_MANAGE_GROUP_LISTING, {}),
          getApiCall(API_COMPANY_LIST, {}),
        ]);
        setGroups(Array.isArray(groupsRes) ? groupsRes : []);
        setAllCompanies(Array.isArray(companiesRes) ? companiesRes : []);
      } catch (err) {
        toast.error(friendlyApiError(err));
      } finally {
        setInitialLoading(false);
      }
    })();
  }, []);

  const isSuperAdmin = getUser()?.role === USER_ROLES.SUPERADMIN;
  const { canRead } = useModuleAcl(MODULE_ACL.GROUPS);
  const canAdd = isSuperAdmin;
  const canEdit = isSuperAdmin;
  const canDelete = isSuperAdmin;
  const visibleTabs = isSuperAdmin ? TABS : TABS.filter((t) => t.id === 'groups');
  const activeTab = isSuperAdmin ? tab : 'groups';

  const refetchGroups = async () => {
    try {
      const data: any = await getApiCall(API_MANAGE_GROUP_LISTING, {});
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(friendlyApiError(err));
    }
  };

  if (!canRead) {
    return (
      <FillPage>
        <div className="my-12 text-center text-base">
          No read permission found in assigned ACL
        </div>
      </FillPage>
    );
  }

  if (initialLoading) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Dialer Groups"
        description="Manage dialer groups and their company assignments."
      />

      {/* Segmented tab strip */}
      <div className="mt-4 inline-flex h-10 items-center gap-1 rounded-full border border-border/60 bg-card p-1 shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)]">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={
                'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs font-medium transition-colors ' +
                (active
                  ? 'bg-primary text-primary-foreground shadow-[0_2px_5px_rgba(0,0,0,0.15)]'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground')
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {activeTab === 'groups' && (
          <GroupsTab
            groups={groups}
            navigate={navigate}
            canAdd={canAdd}
            canEdit={canEdit}
            canDelete={canDelete}
            refetchGroups={refetchGroups}
          />
        )}
        {activeTab === 'assign' && (
          <AssignTab
            groups={groups}
            companies={allCompanies}
          />
        )}
        {activeTab === 'remove' && (
          <RemoveTab
            groups={groups}
            companies={allCompanies}
          />
        )}
      </div>
    </FillPage>
  );
}

/* ── Groups tab ──────────────────────────────────────────────────────── */
function GroupsTab({ groups, canAdd, canEdit, canDelete, refetchGroups }: any) {
  const [searchValue, setSearchValue] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<any[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<any>({ id: null, name: '' });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

  const [exportBusy, setExportBusy] = useState(false);

  const filtered = useMemo(
    () =>
      groups.filter((g: any) =>
        (g?.group_name || '').toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [groups, searchValue],
  );

  const toggleRow = (id: any) => {
    setSelectedGroups((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    const ids = filtered.map((g: any) => g.group_id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedGroups.includes(id));
    if (allSelected) {
      setSelectedGroups((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedGroups((prev) => Array.from(new Set([...prev, ...ids])));
    }
  };

  async function handleCreate() {
    if (!newName.trim()) {
      toast.warning('Group name is required');
      return;
    }
    setSaving(true);
    try {
      await postApiCall(API_MANAGE_GROUP_CREATE, { group_name: newName.trim() });
      toast.success('Dialer group created successfully');
      setCreateOpen(false);
      setNewName('');
      await refetchGroups();
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!editGroup.name.trim()) {
      toast.warning('Group name is required');
      return;
    }
    try {
      await postApiCall(API_MANAGE_GROUP_UPDATE(editGroup.id), {
        group_name: editGroup.name.trim(),
      });
      toast.success('Dialer group updated successfully');
      setEditOpen(false);
      await refetchGroups();
    } catch (err) {
      toast.error(friendlyApiError(err));
    }
  }

  async function executeDelete() {
    setDeleting(true);
    try {
      if (bulkDeleteMode) {
        await Promise.all(
          selectedGroups.map((id) => postApiCall(API_MANAGE_GROUP_DELETE, { id })),
        );
        toast.success('Dialer groups deleted successfully');
        setSelectedGroups([]);
      } else {
        await postApiCall(API_MANAGE_GROUP_DELETE, { id: deleteTarget });
        toast.success('Dialer group deleted successfully');
        setSelectedGroups((prev) => prev.filter((id) => id !== deleteTarget));
      }
      await refetchGroups();
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
      setDeleteTarget(null);
      setBulkDeleteMode(false);
    }
  }

  async function handleExport() {
    setExportBusy(true);
    try {
      exportToCSV({
        fileName: 'dialer-groups',
        data: filtered,
        columns: [
          { header: 'Group Id', accessor: 'group_id' },
          { header: 'Group Name', accessor: 'group_name' },
          { header: 'Created At', accessor: (row: any) => formatDateTime(row.created_at) },
        ],
      });
    } finally {
      setExportBusy(false);
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={filtered.length > 0 && filtered.every((g: any) => selectedGroups.includes(g.group_id))}
          onCheckedChange={toggleAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedGroups.includes(row.original.group_id)}
          onCheckedChange={() => toggleRow(row.original.group_id)}
          aria-label="Select row"
        />
      ),
    },
    { accessorKey: 'group_id', header: 'Group Id' },
    { accessorKey: 'group_name', header: 'Group Name' },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEditGroup({ id: row.original.group_id, name: row.original.group_name });
                setEditOpen(true);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
              title="Edit"
              aria-label="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row.original.group_id);
                setBulkDeleteMode(false);
                setDeleteOpen(true);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
              title="Delete"
              aria-label="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by group name…"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="h-10 w-full rounded-md border border-input bg-card pl-9 pr-3 text-sm text-foreground outline-none focus:border-ring"
          />
        </div>
        <div className="flex items-center gap-2">
          {canDelete && selectedGroups.length > 0 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                setBulkDeleteMode(true);
                setDeleteOpen(true);
              }}
              className="gap-1.5"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete Selected ({selectedGroups.length})
            </Button>
          )}
          {groups.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleExport}
              disabled={exportBusy}
              className="gap-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              Export
            </Button>
          )}
          {canAdd && (
            <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Create Group
            </Button>
          )}
        </div>
      </div>

      <DataTable
        tableId="manage-groups.list:v2"
        columns={columns}
        data={filtered}
        pageSize={10}
        emptyTitle="No dialer groups found"
        disableExport
      />

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Plus className="h-4 w-4" />
              </span>
              Create Dialer Group
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Group name <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter group name"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} className="gap-2">
              {saving ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Pencil className="h-4 w-4" />
              </span>
              Update Dialer Group
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Group name <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={editGroup.name}
              onChange={(e) => setEditGroup({ ...editGroup, name: e.target.value })}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => !next && !deleting && setDeleteOpen(next)}
        title={bulkDeleteMode ? 'Delete selected groups?' : 'Delete dialer group?'}
        description={
          bulkDeleteMode
            ? `${selectedGroups.length} group(s) will be permanently removed. This action cannot be undone.`
            : 'This group will be permanently removed. This action cannot be undone.'
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={executeDelete}
      />
    </>
  );
}

/* ── Assign Companies tab ────────────────────────────────────────────── */
function AssignTab({ groups, companies }: any) {
  const [selectedGroupId, setSelectedGroupId] = useState<any>('');
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(
    () =>
      companies.filter((c: any) =>
        (c.name || '').toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [companies, searchValue],
  );

  const toggleRow = (id: any) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    const ids = filtered.map((c: any) => c.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    if (allSelected) setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    else setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  async function handleAssign() {
    setBusy(true);
    try {
      await postApiCall(API_BULK_GROUP_ADD, {
        company_ids: selectedIds,
        group_id: selectedGroupId,
      });
      toast.success('Companies assigned to dialer group successfully');
      setSelectedIds([]);
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={filtered.length > 0 && filtered.every((c: any) => selectedIds.includes(c.id))}
          onCheckedChange={toggleAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedIds.includes(row.original.id)}
          onCheckedChange={() => toggleRow(row.original.id)}
          aria-label="Select row"
        />
      ),
    },
    { accessorKey: 'id', header: 'Company Id' },
    { accessorKey: 'name', header: 'Company Name' },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={selectedGroupId ? String(selectedGroupId) : undefined}
            onValueChange={(v) => setSelectedGroupId(v)}
          >
            <SelectTrigger className="h-10 w-[240px]">
              <SelectValue placeholder="Select dialer group…" />
            </SelectTrigger>
            <SelectContent>
              {groups.map((g: any) => (
                <SelectItem key={g.group_id} value={String(g.group_id)}>
                  {g.group_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative w-full max-w-xs">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search company…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-card pl-9 pr-3 text-sm text-foreground outline-none focus:border-ring"
            />
          </div>
        </div>
        <Button
          size="sm"
          disabled={selectedIds.length === 0 || !selectedGroupId || busy}
          onClick={() => setConfirmOpen(true)}
          className="gap-1.5"
        >
          <UserPlus className="h-3.5 w-3.5" />
          Assign to Group ({selectedIds.length})
        </Button>
      </div>

      <DataTable
        tableId="bulk-group-add:v2"
        columns={columns}
        data={filtered}
        pageSize={10}
        emptyTitle="No companies found"
        disableExport
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(next) => !next && !busy && setConfirmOpen(next)}
        title="Assign companies to group?"
        description={`${selectedIds.length} company(s) will be assigned to this dialer group.`}
        confirmLabel="Assign"
        loading={busy}
        onConfirm={handleAssign}
      />
    </>
  );
}

/* ── Remove Companies tab ────────────────────────────────────────────── */
function RemoveTab({ groups, companies }: any) {
  const [selectedGroupId, setSelectedGroupId] = useState<any>('');
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [groupCompanies, setGroupCompanies] = useState<any[]>([]);
  const [groupName, setGroupName] = useState('');
  const [applied, setApplied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filterBusy, setFilterBusy] = useState(false);

  const filtered = useMemo(
    () =>
      groupCompanies.filter((c: any) =>
        (c.name || '').toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [groupCompanies, searchValue],
  );

  async function applyFilter() {
    if (!selectedGroupId) {
      toast.warning('Please select a group first');
      return;
    }
    setFilterBusy(true);
    try {
      const groupCos: any = await getApiCall(API_GET_COMPANY_GROUP(String(selectedGroupId)), {});
      const ids = new Set((groupCos || []).map((x: any) => Number(x.company_id)));
      const list = (companies || []).filter((c: any) => ids.has(Number(c.id)));
      setGroupCompanies(list);
      setSelectedIds([]);
      setApplied(true);
      const g = (groups || []).find((x: any) => Number(x.group_id) === Number(selectedGroupId));
      setGroupName(g?.group_name || '');
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setFilterBusy(false);
    }
  }

  function clearFilter() {
    setGroupCompanies([]);
    setSelectedIds([]);
    setSearchValue('');
    setApplied(false);
    setGroupName('');
    setSelectedGroupId('');
  }

  const toggleRow = (id: any) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    const ids = filtered.map((c: any) => c.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    if (allSelected) setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    else setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  async function handleRemove() {
    setBusy(true);
    try {
      await postApiCall(API_BULK_GROUP_DELETE, {
        company_ids: selectedIds,
        group_id: selectedGroupId,
      });
      toast.success('Companies removed from group successfully');
      await applyFilter();
      setSelectedIds([]);
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={filtered.length > 0 && filtered.every((c: any) => selectedIds.includes(c.id))}
          onCheckedChange={toggleAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedIds.includes(row.original.id)}
          onCheckedChange={() => toggleRow(row.original.id)}
          aria-label="Select row"
        />
      ),
    },
    { accessorKey: 'id', header: 'Company Id' },
    { accessorKey: 'name', header: 'Company Name' },
    { id: 'group_name', header: 'Group', cell: () => groupName || '—' },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={selectedGroupId ? String(selectedGroupId) : undefined}
            onValueChange={(v) => setSelectedGroupId(v)}
          >
            <SelectTrigger className="h-10 w-[240px]">
              <SelectValue placeholder="Select dialer group…" />
            </SelectTrigger>
            <SelectContent>
              {groups.map((g: any) => (
                <SelectItem key={g.group_id} value={String(g.group_id)}>
                  {g.group_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={applyFilter} disabled={filterBusy}>
            Load members
          </Button>
          <Button size="sm" variant="ghost" onClick={clearFilter}>
            Clear
          </Button>
          {applied && (
            <div className="relative w-full max-w-xs">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search company…"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-card pl-9 pr-3 text-sm text-foreground outline-none focus:border-ring"
              />
            </div>
          )}
        </div>
        <Button
          size="sm"
          variant="destructive"
          disabled={selectedIds.length === 0 || !selectedGroupId || busy}
          onClick={() => setConfirmOpen(true)}
          className="gap-1.5"
        >
          <UserMinus className="h-3.5 w-3.5" />
          Remove ({selectedIds.length})
        </Button>
      </div>

      {!applied ? (
        <div className="rounded-lg border border-dashed border-border/60 bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          Select a dialer group and click <strong>Load members</strong> to see the assigned
          companies.
        </div>
      ) : (
        <DataTable
          tableId="bulk-group-delete:v2"
          columns={columns}
          data={filtered}
          pageSize={10}
          emptyTitle="No companies in this group"
          disableExport
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(next) => !next && !busy && setConfirmOpen(next)}
        title="Remove companies from group?"
        description={`${selectedIds.length} company(s) will be removed from this dialer group.`}
        confirmLabel="Remove"
        tone="destructive"
        loading={busy}
        onConfirm={handleRemove}
      />
    </>
  );
}
